import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { CheckIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

import { useDebounce } from "@/hooks/use-debounce";

import type { TableFilterOption } from "@/types";

interface SearchSelectFilterProps {
  /** Joriy qiymat — doim satr: URL holati faqat satrni saqlaydi. */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  /** To'liq ro'yxat — qidiruv shu ro'yxat ichida. */
  options?: TableFilterOption[];
  /** Server qidiruvi — har qidiruv matni uchun so'rov. */
  getQuery?: (input: string) => {
    queryKey: unknown[];
    queryFn: () => Promise<TableFilterOption[]>;
  };
  /** Yuklangan ro'yxatda yo'q qiymatning nomi (URL'dan kelgan id). */
  getLabel?: (value: string) => { queryKey: unknown[]; queryFn: () => Promise<string | null> };
}

const toKey = (value: TableFilterOption["value"]) => String(value);

/**
 * Sarlavhadagi qidiruvli tanlov filtri.
 *
 * Qiymat obyekt emas, ID SATRI bo'lib beriladi: URL holati faqat satrni
 * yozadi va sahifa yangilanganda filtr yo'qolmaydi. Nomni esa ro'yxatdan
 * (yoki `getLabel` dan) o'zi topadi.
 *
 * Ro'yxat sarlavha popover'ining o'zida chiziladi, ichma-ich ikkinchi popover
 * emas. Tanlangan variant ro'yxat boshida ajratilib turadi, "Tozalash" esa
 * pastda — qidiruv maydoni bilan natijalar orasiga hech narsa tushmaydi.
 */
export function SearchSelectFilter({
  value,
  onChange,
  options,
  getQuery,
  getLabel,
}: Readonly<SearchSelectFilterProps>) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const remoteQuery = getQuery?.(debouncedSearch);
  const remote = useQuery({
    queryKey: remoteQuery?.queryKey ?? ["search-select-filter", "static"],
    queryFn: remoteQuery?.queryFn ?? (async () => [] as TableFilterOption[]),
    enabled: !!remoteQuery,
  });

  const labelQuery = value && getLabel ? getLabel(value) : undefined;

  /* Bosib tanlangan variantning nomi eslab qolinadi: qidiruv matni o'zgarib,
     tanlangan variant ro'yxatdan chiqib ketganda ham tepada nomi turadi. */
  const [picked, setPicked] = useState<{ value: string; label: string } | null>(null);

  const query = search.trim().toLocaleLowerCase();
  const visible = options
    ? options.filter(option => option.label.toLocaleLowerCase().includes(query))
    : (remote.data ?? []);

  const selectedOption = [...(options ?? []), ...(remote.data ?? [])].find(
    option => toKey(option.value) === value,
  );

  const labelOfSelected = useQuery({
    queryKey: labelQuery?.queryKey ?? ["search-select-filter", "label", value],
    queryFn: labelQuery?.queryFn ?? (async () => null),
    enabled: !!labelQuery && !selectedOption && picked?.value !== value,
  });

  let selectedLabel: string | undefined;
  if (value) {
    if (selectedOption) selectedLabel = selectedOption.label;
    else if (picked?.value === value) selectedLabel = picked.label;
    else selectedLabel = labelOfSelected.data ?? value;
  }

  /* Tanlangan variant ro'yxatning boshiga qadaladi va pastdagi ro'yxatda
     takrorlanmaydi: qidiruv natijasida bo'lmasa ham ko'rinib turadi, bosilsa
     filtr olib tashlanadi. */
  const rest = visible.filter(option => toKey(option.value) !== value);

  return (
    <Command shouldFilter={false} className="w-72">
      <CommandInput value={search} onValueChange={setSearch} placeholder={t("TABLE.SEARCH")} />

      <CommandList>
        <CommandEmpty>{t("TABLE.NO_DATA")}</CommandEmpty>

        {value && (
          <>
            <CommandGroup>
              <CommandItem
                value={`selected:${value}`}
                onSelect={() => onChange(undefined)}
                className="bg-primary/10 text-primary data-[selected=true]:bg-primary/15 data-[selected=true]:text-primary font-medium"
              >
                <CheckIcon className="text-primary size-4" />
                <span className="truncate">{selectedLabel}</span>
              </CommandItem>
            </CommandGroup>
            {rest.length > 0 && <CommandSeparator />}
          </>
        )}

        {rest.length > 0 && (
          <CommandGroup>
            {rest.map(option => {
              const key = toKey(option.value);

              return (
                <CommandItem
                  key={key}
                  value={key}
                  onSelect={() => {
                    setPicked({ value: key, label: option.label });
                    onChange(key);
                  }}
                >
                  {/* Belgi o'rni bo'sh qoldiriladi — matnlar tanlangan qator
                      bilan bir chiziqda tursin. */}
                  <span className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>

      {/* Tozalash pastda — faqat filtr qo'yilganda ko'rinadi. */}
      {value && (
        <div className="border-t p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground w-full"
            onClick={() => onChange(undefined)}
          >
            <XIcon />
            {t("TABLE.CLEAR")}
          </Button>
        </div>
      )}
    </Command>
  );
}
